import type { EButtonStyleType, EFontStyle, ESize } from '@common/enums';
import type { ThemedBaseButtonProp } from '@common/types/base-button-widget.type';
import type { JssStyle } from 'jss';
import type { FormButtonSettings } from '@components/sp-form-button-settings/src/sp-form-button-settings.type';
import { type ButtonIcon } from '@components/sp-button-icon/type/button-icon.type';
import type { RecurringMode } from '@web/widgets/product-card/type';

export type CatalogProductsWidgetData = {
    selectedCategory: number;
    catalogView: CatalogCategoriesView;
    itemsInRow: {
        desktop: number;
        mobile: number;
    };
    maxPerPage: number;
    basketButton: ButtonSettings;
    pagination: PaginationSettings;
    filter: FilterSettings;
    sorting: SortingSettings;
    settings: Settings;
    userHash: string;
    categories: CatalogCategory[];
    products: CatalogProductCard[];
    total: number;
};

export type Settings = {
    general: GeneralSettings;
    card: CardSettings;
    gallery: GallerySettings;
    typography: TypographySettings;
};

export type GeneralSettings = {
    margin: number;
};

export type CardSettings = {
    backgroundColor: string;
    borderRadius: number;
    padding: number;
    isBorder: boolean;
    borderWidth: ProductStyleSettings['borderWidth'];
    borderStyle: ProductStyleSettings['borderStyle'];
    borderColor: ProductStyleSettings['borderColor'];
};

export type GallerySettings = {
    aspectRatio: AspectRatio;
    attachment: Attachment;
    backgroundColor: string;
    borderRadius: number;
    margin: number;
};

export type TypographySettings = {
    titleSettings: {
        color: string;
        fontSize: number;
        fontStyle: Array<EFontStyle>;
        isShow?: boolean;
    };
    counterSettings: {
        color: string;
        fontSize: number;
        fontStyle: Array<EFontStyle>;
        isShow?: boolean;
    };
    priceSettings: {
        color: string;
        fontSize: number;
        fontStyle: Array<EFontStyle>;
        isShow?: boolean;
    };
    oldPriceSettings: {
        color: string;
        fontSize: number;
        fontStyle: Array<EFontStyle>;
        isShow?: boolean;
    };
};

export type SortingSettings = {
    enable: boolean;
    borderStyle: ProductStyleSettings['borderStyle'];
    textColor: any;
    backgroundColor: any;
    borderColor: any;
    borderRadius: number;
    inputSize: ESize;
    searchEnable: boolean;
    searchPlaceholder: string;
};

export type FilterSettings = {
    general: GeneralFilterSettings;
    category: CategoryFilterSettings;
    price: PriceFilterSettings;
};

export type GeneralFilterSettings = {
    backgroundColor: any;
    borderRadius: number;
    isBorder: boolean;
    borderWidth: ProductStyleSettings['borderWidth'];
    borderStyle: ProductStyleSettings['borderStyle'];
    borderColor: ProductStyleSettings['borderColor'];
    paddingTop: ProductStyleSettings['paddingTop'];
    paddingBottom: ProductStyleSettings['paddingBottom'];
    paddingLeft: ProductStyleSettings['paddingLeft'];
    paddingRight: ProductStyleSettings['paddingRight'];
};

export type CategoryFilterSettings = {
    label: string;
    categoryLabelFont: {
        color: string;
        fontSize: number;
        fontStyle: Array<EFontStyle>;
        isShow?: boolean;
    };
    categoryFont: {
        color: string;
        fontSize: number;
        fontStyle: Array<EFontStyle>;
        isShow?: boolean;
    };
    selectedCategoryFont: {
        color: string;
        fontSize: number;
        fontStyle: Array<EFontStyle>;
        isShow?: boolean;
    };
    selectFilterSettings: {
        borderStyle: ProductStyleSettings['borderStyle'];
        textColor: any;
        backgroundColor: any;
        borderColor: any;
        borderRadius: number;
        inputSize: ESize;
    };
};

export type PriceFilterSettings = {
    label: string;
    priceLabelFont: {
        color: string;
        fontSize: number;
        fontStyle: Array<EFontStyle>;
        isShow?: boolean;
    };
    borderStyle: ProductStyleSettings['borderStyle'];
    textColor: any;
    backgroundColor: any;
    borderColor: any;
    borderRadius: number;
    inputSize: ESize;
    slider: boolean;
    colorLine: string;
    colorFilledLine: string;
    colorButtons: string;
};

export type PaginationSettings = {
    pagesButtons: {
        textColor: any;
        backgroundColor: any;
        borderColor: any;
        borderRadius: number;
        inputSize: ESize;
    };
    currentPage: {
        textColor: any;
        backgroundColor: any;
        borderColor: any;
    };
    nextPage: FormButtonSettings;
};

export type ButtonSettings = {
    label: string;
    action: ButtonActionType;
    styleType: EButtonStyleType;
    size: ESize;
    buttonBase: ThemedBaseButtonProp;
    buttonHover?: any;
    isShowButton?: boolean;
    buttonIcon: ButtonIcon;
};

export enum CatalogCategoriesView {
    left,
    top,
}

export type CatalogCategory = {
    id: number;
    name: string;
    parent_id: number;
    depth: number;
    path: number | string;
    children?: CatalogCategory[];
};

export enum CatalogProductType {
    physical = 1,
    digital = 2,
}

export type CatalogProductCard = {
    availability: number;
    balance: {
        type: number;
        value: number;
    };
    categoryId: number;
    categoryName: string;
    categorySections: Array<{
        categoryId: number;
        id: number;
        name: string;
        order: number;
        values: Array<{
            id: number;
            sectionId: number;
            key: string;
            value: string;
        }>;
    }>;
    description: string;
    id: number;
    images: string[];
    name: string;
    prices: Array<{
        id: number;
        currency: string;
        isDefault: boolean;
        value: number;
        oldValue: number;
        productId: number;
    }>;
    sections: Array<{
        id: number;
        name: string;
        productId: number;
        values: Array<{
            id: number;
            sectionId: number;
            key: string;
            value: string;
        }>;
    }>;
    seo: {
        name: string;
        description: string;
        keys: string;
    };
    shortDescription: string;
    type: number;
    userId: number;
    visible: number;
    vendorCode: string;
    productType: CatalogProductType;
    taxes: Array<{
        id: number;
        name: string;
        value: number;
        isAddedToAmount: boolean;
    }>;
    slug: string;
    recurringMode: RecurringMode;
};

export type ProductStyleSettings = Pick<
    JssStyle,
    'borderWidth' | 'borderStyle' | 'borderColor' | 'paddingTop' | 'paddingBottom' | 'paddingLeft' | 'paddingRight'
>;

export enum AspectRatio {
    default = 'default',
    manual = 'manual',
    onoToOne = '1:1',
    wide = '16:9',
    verticalWide = '9:16',
    fourToThree = '4:3',
    threeToFour = '3:4',
    threeToTwo = '3:2',
    twoToThree = '2:3',
}

export enum Attachment {
    cover = 'cover',
    contain = 'contain',
}

export enum ButtonActionType {
    basket,
    modal,
}
